import type { Profile } from '../shared/types';
import { apiUrl } from './config';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, status: number) {
    super(code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.token) headers.Authorization = `Bearer ${options.token}`;

  const response = await fetch(apiUrl(path), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new ApiError(
      typeof data.error === 'string' ? data.error : 'REQUEST_FAILED',
      response.status,
    );
  }
  return data as T;
}

export async function fetchMe(token: string): Promise<Profile> {
  const data = await request<{ profile: Profile }>('/api/me', { token });
  return data.profile;
}

export async function fetchLeaderboard(limit = 20): Promise<Profile[]> {
  const data = await request<{ leaders: Profile[] }>(`/api/leaderboard?limit=${limit}`);
  return data.leaders;
}

export async function updateDisplayName(token: string, displayName: string): Promise<Profile> {
  const data = await request<{ profile: Profile }>('/api/profile', {
    method: 'PATCH',
    token,
    body: { displayName },
  });
  return data.profile;
}

export async function createRoom(token: string): Promise<{ code: string; profile: Profile }> {
  return request<{ code: string; profile: Profile }>('/api/room/create', {
    method: 'POST',
    token,
    body: {},
  });
}

export async function joinRoom(
  token: string,
  code: string,
): Promise<{ code: string; profile: Profile }> {
  return request<{ code: string; profile: Profile }>('/api/room/join', {
    method: 'POST',
    token,
    body: { code },
  });
}

export async function fetchArchive(
  token: string,
): Promise<Record<string, number>> {
  const data = await request<{ fragments: { id: string; duplicates: number }[] }>(
    '/api/archive',
    { token },
  );
  const result: Record<string, number> = {};
  for (const fragment of data.fragments) result[fragment.id] = fragment.duplicates;
  return result;
}

export async function unlockArchiveFragment(
  token: string,
  fragmentId: string,
): Promise<{ duplicate: boolean; duplicates: number }> {
  return request<{ ok: boolean; duplicate: boolean; duplicates: number }>(
    '/api/archive/unlock',
    {
      method: 'POST',
      token,
      body: { fragmentId },
    },
  );
}
